/**
 * TruePrice V1 - Constants Exports
 */

export { PSI_BASE, MASTER_PSI, getPSIBase } from './psi';
export { CHANNEL_COMMISSION, SALES_CHANNEL_KEYS, computeRetailPrice } from './channels';
export type { SalesChannelKey, ChannelConfig } from './channels';
export { SUBSTRATE_MODIFIERS, getSubstrateGroup, getDefaultSubstrate, getSubstrateModifier, getSubstrateOptions } from './substrates';
export type { SubstrateConfig, SubstrateGroup } from './substrates';
export { PHASES, getSizeScale, FIDELITY_SCALE, BASE_HOURS, estimateHours } from './phases';
export type { PhaseItem } from './phases';
export { WIZARD_MATERIALS, MATERIAL_ESTIMATES, estimateMaterialCents, FRAMING_BRACKETS, getFramingEstimate, getFramingEstimateCents } from './materials';
export type { MaterialItem, MaterialEstimateConfig, FramingBracket } from './materials';

export {
  SIZE_ANCHORS,
  SIZE_THRESHOLDS,
  interpolateSizeMultiplier,
  getDetailPremium,
} from './size';

/**
 * Density relaxer factor for paper-based mediums
 * Source: Addendum E Section 3.4
 */
export const DENSITY_RELAXER_FACTOR = 0.70; // 30% reduction

/**
 * Material exception threshold
 * Source: Addendum E Section 3.2
 */
export const MATERIAL_EXCEPTION_THRESHOLD = 0.40; // 40% of gutPrice (falls back to TTCP)

/**
 * Minimum floor multiplier for micro works
 * Source: Addendum E Section 1.2
 */
export const MINIMUM_FLOOR_MULTIPLIER = 1.5;

/**
 * Default material cost estimates per medium (cents version for legacy consumers).
 * Source: P7 remediation — aligned with MATERIAL_ESTIMATES in materials.ts.
 * M_default = Base (cents) + Rate (cents/sqin) × Area
 * Prefer using estimateMaterialCents() from materials.ts for new code.
 */
export const MATERIAL_COST_DEFAULTS: Record<string, { base: number; rate: number }> = {
  oil:         { base: 2500, rate: 12 },    // $25 + $0.12/sqin
  acrylic:     { base: 1800, rate: 8 },     // $18 + $0.08/sqin
  watercolor:  { base: 1500, rate: 4 },     // $15 + $0.04/sqin
  gouache:     { base: 1500, rate: 4 },     // $15 + $0.04/sqin
  pastel:      { base: 2000, rate: 6 },     // $20 + $0.06/sqin
  mixed_media: { base: 2000, rate: 10 },    // $20 + $0.10/sqin
};

/**
 * Medium-specific material checklists for info bottom sheet
 */
export const MATERIAL_CHECKLISTS: Record<string, string[]> = {
  oil:         ['Canvas or panel', 'Oil paints', 'Mediums and solvents', 'Varnish', 'Brushes (pro-rated)'],
  acrylic:     ['Canvas or panel', 'Acrylic paints', 'Gesso and mediums', 'Varnish', 'Brushes (pro-rated)'],
  watercolor:  ['Watercolor paper', 'Watercolor paints', 'Masking fluid', 'Brushes (pro-rated)'],
  gouache:     ['Paper or board', 'Gouache paints', 'Fixative', 'Brushes (pro-rated)'],
  pastel:      ['Pastel paper or sanded surface', 'Soft pastels', 'Hard pastels', 'Fixative'],
  mixed_media: ['Substrate', 'Primary medium', 'Secondary materials', 'Adhesives and mediums', 'Sealant'],
};

/**
 * Inline helper text per medium (shown below materials input)
 */
export const MATERIAL_HELPERS: Record<string, string> = {
  oil:         'Canvas, oil paints, mediums, varnish',
  acrylic:     'Canvas, acrylic paints, gesso, varnish',
  watercolor:  'Paper, watercolor paints, masking fluid',
  gouache:     'Paper, gouache paints, fixative',
  pastel:      'Sanded paper, soft pastels, fixative',
  mixed_media: 'Substrate, primary and secondary materials',
};
